'use client';

import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  QueryObserverOptions,
  defaultShouldDehydrateQuery,
  dehydrate,
  hydrate,
} from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { queryClientAtom } from 'jotai-tanstack-query';
import { useHydrateAtoms } from 'jotai/utils';
import { PropsWithChildren, useEffect, useRef } from 'react';

import { useLocalStorage } from '@flipster/react/misc/state/use-local-storage';
import { checkMaintenanceError } from '@flipster/utils/axios';
import { isServer, isTest } from '@flipster/utils/env-helper';

import { authBunja } from '../shared/auth/state';

interface BaseProps extends PropsWithChildren {
  options?: Partial<QueryObserverOptions>;
  queryCache?: QueryCache;
  allowPersist?: boolean;
}

function createQueryClient(cache: QueryCache | undefined, options?: Partial<QueryObserverOptions>) {
  return new QueryClient({
    queryCache: cache,
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        ...options,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient(cache: QueryCache | undefined, options?: Partial<QueryObserverOptions>) {
  if (isServer || isTest) {
    return createQueryClient(cache, options);
  } else {
    return (browserQueryClient ??= createQueryClient(cache, options));
  }
}

const currentVersion = process.env.NEXT_PUBLIC_VERSION || 'dev';
export const ReactQueryProvider = ({
  allowPersist = false,
  options,
  queryCache: clientQueryCache,
  children,
}: BaseProps) => {
  const queryClient = getQueryClient(clientQueryCache, options);
  useHydrateAtoms([[queryClientAtom, queryClient]]);

  const cacheHydrated = useRef(false);
  const [queryCache, setQueryCache] = useLocalStorage({
    key: 'flipster.configs.v1.query.cache',
    defaultValue: {
      version: currentVersion,
      mutations: [],
      queries: [],
    },
  });

  useEffect(() => {
    if (!allowPersist) return;
    if (queryCache != null && queryCache.version !== currentVersion) {
      cacheHydrated.current = true;
      setQueryCache({
        version: currentVersion,
        mutations: [],
        queries: [],
      });
      return;
    }

    if (!cacheHydrated.current && queryCache.queries.length > 0) {
      cacheHydrated.current = true;
      hydrate(queryClient, queryCache);
    }
  }, [allowPersist, queryCache, queryClient, setQueryCache]);

  useEffect(() => {
    if (!allowPersist) return;
    const cache = queryClient.getQueryCache();
    return cache.subscribe((event) => {
      const cacheEventTypes = ['added', 'removed', 'updated'];
      if (!cacheEventTypes.includes(event.type)) {
        return;
      }
      const dehydratedState = dehydrate(queryClient, {
        shouldDehydrateQuery: (query) => defaultShouldDehydrateQuery(query) && query.isActive(),
        shouldDehydrateMutation: () => false,
      });
      setQueryCache({
        version: currentVersion,
        ...dehydratedState,
      });
    });
  }, [allowPersist, queryClient, setQueryCache]);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

interface Props extends PropsWithChildren {
  isBuild?: boolean;
}

export const WebviewReactQueryProvider = ({ isBuild, children }: Props) => {
  return (
    <ReactQueryProvider
      allowPersist
      options={{
        refetchOnWindowFocus: isBuild,
        staleTime: isBuild ? 30 * 1000 : undefined,
      }}
    >
      {children}
    </ReactQueryProvider>
  );
};

export const WebReactQueryProvider = ({ isBuild, children }: Props) => {
  const { checkSessionError } = useBunja(authBunja);

  return (
    <ReactQueryProvider
      queryCache={
        new QueryCache({
          onError: (error) => {
            checkSessionError(error);
          },
        })
      }
      options={{
        refetchOnWindowFocus: isBuild,
        staleTime: isBuild ? 30 * 1000 : undefined,
        throwOnError(err) {
          // 503은 무조건 해주기 503의 message 가 없어야 점검중
          if (!isServer && window.location.pathname.includes('/maintenance')) return false;
          if (checkMaintenanceError(err)) return true;
          return false;
        },
      }}
    >
      {children}
    </ReactQueryProvider>
  );
};
